import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "../../navigation/Navbar/Navbar";
import { SandboxBanner } from "../../navigation/SandboxBanner";
import { SuperAdminRoutesMap } from "./SuperAdminRoutes";
import SuperAdminRoute from "../../../Routegaurd/SuperAdminRouteGuard";
import PlanningRouteGuard from "../../../Routegaurd/PlanningRouteGuard";
import TrainingAuthorRouteGuard from "../../../Routegaurd/TrainingAuthorRouteGuard";
import { useAuth } from "../../../firebase/AuthProvider";
import { applyDocumentTheme, getSystemTheme, LAST_AUTHENTICATED_THEME_KEY } from "../../../utilities/themePreference";
import { BrandLoader } from "../../ui/Logo/BrandLoader";
import { DashboardLoadingTemplate } from "../dashboard-Auth2/components/DashboardLoadingTemplate";
import { ROLES } from "../../../Data/SD";

const TrainingPortal = React.lazy(() => import("../training/TrainingPortal"));
const ResourceDirectory = React.lazy(() => import("../resourceDirectory/ResourceDirectory"));
const TrainingManage = React.lazy(() => import("../training/TrainingManage"));
const MachineIdentities = React.lazy(() => import("../machines/MachineIdentities"));
const CalendarSubscriptionPage = React.lazy(() => import("../settings/CalendarSubscriptionPage"));
const TrainingStudio = React.lazy(() => import("../training/TrainingStudio"));
const Reports = React.lazy(() => import("../reports/Reports"));
const AgencyReportsPage = React.lazy(() => import("../reportRecords/AgencyReportsPage").then(module => ({default: module.AgencyReportsPage})));
const Profile = React.lazy(() => import("../profile/Profile"));
const TaskPage = React.lazy(() => import("../taskPage/TaskPage"));
const NewFileRequirementForm = React.lazy(() => import("../taskPage/taskAttachments/NewFileRequirementForm"));
const RecurringTaskCreation = React.lazy(() => import("../task/taskCreation/RecurringTaskCreation"));
const TaskCreationSelector = React.lazy(() => import("../task/taskCreation/TaskCreationSelector"));
const EditRecurringTasksPage = React.lazy(() => import("../task/taskEdit/EditRecurringTasksPage"));
const EditWorkflows = React.lazy(() => import("../workflow/EditWorkflows"));
const EditParticipantPage = React.lazy(() => import("../participants/EditParticipantPage"));
const FileViewPage = React.lazy(() => import("../fileViewer/FileViewPage"));
const CreateParticipantPage = React.lazy(() => import("../participants/CreateParticipantPage"));
const TeamMemberDashboard = React.lazy(() => import("../dashboard/TeamMemberDashboard"));
const CreateTaskActivityComponent = React.lazy(() => import("../taskActivity/CreateTaskActivityComponent"));
const TaskCreationPage = React.lazy(() => import("../task/taskCreation/TaskCreationPage"));
const EditTemplateFiles = React.lazy(() => import("../templateFiles/EditTemplateFiles"));
const AddTemplateFile = React.lazy(() => import("../templateFiles/AddTemplateFile"));
const CreateNewTaskActivityType = React.lazy(() => import("../taskActivity/CreateNewTaskActivityType"));
const CreateNewTaskActivityPage = React.lazy(() => import("../taskActivity/CreateNewTaskActivityPage"));
const EmployeeTasksPageComponent = React.lazy(() => import("../TasksPage/EmployeeTasksPageComponent"));
const TasksPageComponent = React.lazy(() => import("../TasksPage/TasksPageComponent"));
const BumpParticipantForm = React.lazy(() => import("../participants/BumpParticipantForm"));
const UserFileViewPage = React.lazy(() => import("../fileViewer/UserFileViewPage"));
const SuggestionRulesComponent = React.lazy(() => import("../generatedSuggestions/SuggestionRulesComponent"));
const TagList = React.lazy(() => import("../task/tagCreation/TagList"));
const TagCreationForm = React.lazy(() => import("../task/tagCreation/TagCreationForm"));
const UserProfile = React.lazy(() => import("../userComponents/UserProfile"));
const ChatPage = React.lazy(() => import("../realtimeChatComponents/ChatPage"));
const PageNotFound = React.lazy(() => import("../errorPages/PageNotFound"));
const Unathorized = React.lazy(() => import("../errorPages/Unauthorized"));
const PDFEditor = React.lazy(() => import("../fileViewer/pdfViewer/PDFEditor"));
const OwnerDashboard = React.lazy(() => import("../dashboard/OwnerDashboard/OwnerDashboard"));
const FormPage = React.lazy(() => import("../formFillComponents/FormPage").then((module) => ({ default: module.FormPage })));
const FormBuilder = React.lazy(() => import("../formFillComponents/formBuilder/FormBuilder"));
const MappingBuilder = React.lazy(() => import("../formFillComponents/mappingBuilder/MappingBuilder"));
const FormAdminPanel = React.lazy(() => import("../formFillComponents/adminPanel/FormAdminPanel"));
const MyCalendar = React.lazy(() => import("../calendar/MyCalendar"));
const SignDocument = React.lazy(() => import("../signature/SignDocument"));
const AuthenticatedSignPage = React.lazy(() => import("../fileViewer/pdfViewer/AuthenticatedSignPage"));
const CaseFileViewPage = React.lazy(() => import("../fileViewer/CaseFileViewPage"));
const ViewFile = React.lazy(() => import("../ViewFiles/ViewFile"));
const Notificationtab = React.lazy(() => import("../notificationtab/Notificationtab"));
const EmployeeFiles = React.lazy(() => import("../profile/EmployeeFiles"));
const WeeklySupervisionList = React.lazy(() => import("../weeklySupervision/WeeklySupervisionList"));
const WeeklySupervisionForSupervisor = React.lazy(() => import("../weeklySupervision/WeeklySupervisionForSupervisor"));
const Forbidden = React.lazy(() => import("../forbidden/Forbidden"));
const Commingsoon = React.lazy(() => import("../commingsoon/Commingsoon"));
const Privacypolicy = React.lazy(() => import("../privacypolicy/Privacypolicy"));
const Resourcerequest = React.lazy(() => import("../resourcerequest/Resourcerequest"));
const Caseloadindependence = React.lazy(() => import("../caseloadindependence/Caseloadindependence"));
const Employeesurveys = React.lazy(() => import("../employeesurveys/Employeesurveys"));
const Surveys = React.lazy(() => import("../surveys/Surveys"));
const Prestormsurvey = React.lazy(() => import("../prestormsurvey/Prestormsurvey"));
const Relocationsurvey = React.lazy(() => import("../relocationsurvey/Relocationsurvey"));
const Poststormsurvey = React.lazy(() => import("../poststormsurvey/Poststormsurvey"));
const Cir = React.lazy(() => import("../cir/Cir"));
const Audittab = React.lazy(() => import("../audittab/Audittab"));
const Settings = React.lazy(() => import("../settings/Settings"));
const ParticipantActivityPage = React.lazy(() => import("../ParticipantActivityPage/ParticipantActivityPage"));
const ReportsPage = React.lazy(() => import("../Samples/ReportsPage"));
const TermsOfService = React.lazy(() => import("../../legal/eSign/TermsOfService"));
const Insights = React.lazy(() => import("../insights/Insights"));
const IssuesPageComponent = React.lazy(() => import("../IssuesPage/IssuesPageComponent"));
const IssuePage = React.lazy(() => import("../issuePage/IssuePage"));
const SprintsListPage = React.lazy(() => import("../sprintview/SprintsListPage"));
const SprintDetailPage = React.lazy(() => import("../sprintview/SprintDetailPage"));
const ParticipantSnapshotTab = React.lazy(() => import("../participant-snapshot/ParticipantSnapshotTab"));
const EventCountdown = React.lazy(() => import("../../ui/EventTimer/EventTimer"));
const DriveRoutes = React.lazy(() => import("./DriveRoutes"));
const ExternalFileRequestHeader = React.lazy(() => import("../ExternalFileReqeust/ExternalFileRequestHeader"));
const EmployeeFileViewPage = React.lazy(() => import("../fileViewer/EmployeeFileViewPage"));
const AllMembers = React.lazy(() => import("../AllMemebers/AllMembers"));
const TeamAccessGate = React.lazy(() => import("../teamPage/TeamAccessGate"));
const OfficeManagerDashboard = React.lazy(() => import("../dashboard/OfficeManagerDashboard"));
const AutomationPage = React.lazy(() => import("../automations/AutomationPage"));
const DashboardAuth2 = React.lazy(() => import("../dashboard-Auth2/DashboardAuth2"));
const FormAdminDashboard = React.lazy(() => import("../formsV2/FormAdminDashboard").then((module) => ({ default: module.FormAdminDashboard })));
const ProductionFormFiller = React.lazy(() => import("../formsV2/ProductionFormFiller").then((module) => ({ default: module.ProductionFormFiller })));
const AdminRequestsPanel = React.lazy(() => import("../userComponents/userProfileComponents/AdminRequestsPanel"));
const AutomationAuditDashboard = React.lazy(() => import("../automations/AutomationAuditDashboard"));
const ParticipantsPage = React.lazy(() => import("../participantsPage/ParticipantsPage"));
const EpicsPage = React.lazy(() => import("../epicPage/EpicsPage"));
const OwnerRoleEditor = React.lazy(() => import("../owner/OwnerRoleEditor"));
const AgingReport = React.lazy(() => import("../agingReport/AgingReport"));
const AdvancedAnalyticsDashboard = React.lazy(() => import("../analyticsV2/AdvancedAnalyticsDashboard"));
const AdminFaqAnalytics = React.lazy(() => import("../wiki/admin/AdminFaqAnalytics"));
const AdminFaqPortal = React.lazy(() => import("../wiki/admin/AdminFaqPortal"));
const FaqPortal = React.lazy(() => import("../wiki/FaqPortal"));
const ComplianceSettings = React.lazy(() => import("../settings/ComplianceSettings"));
const ChannelAdminPage = React.lazy(() => import("../channels/ChannelAdminPage"));
const LeaveSettings = React.lazy(() => import("../settings/LeaveSettings"));
const DashboardSettings = React.lazy(() => import("../settings/DashboardSettings"));
const BrandingSettings = React.lazy(() => import("../settings/BrandingSettings"));
const ReportsAnalyticsSettings = React.lazy(() => import("../settings/ReportsAnalyticsSettings"));
const StateReconciliationPage = React.lazy(() => import("../settings/stateReconciliation/StateReconciliationPage"));

const OperationalDashboardRoute = () => {
  const { currentRoles, isLoading } = useAuth();

  if (isLoading || currentRoles === null) {
    return <DashboardLoadingTemplate />;
  }

  const isSuperAdmin = currentRoles.some(
    (role) => role.name?.trim().toUpperCase() === ROLES.SUPER_ADMIN,
  );

  if (isSuperAdmin) {
    return <Navigate to="/dashboard/superadmin" replace />;
  }

  return (
    <React.Suspense fallback={<DashboardLoadingTemplate />}>
      <DashboardAuth2 />
    </React.Suspense>
  );
};

const Home: React.FC = () => {
  const location = useLocation();
  const faqWorkspace = location.pathname === '/faqs' || location.pathname.startsWith('/faqs/');
  const { currentUser } = useAuth();
  const [darkMode, setDarkMode] = useState(false);
  const canUseDarkMode = Boolean(currentUser);
  const darkModeStorageKey = currentUser
    ? `darkMode:${currentUser.uid}`
    : null;
  const privilegedDarkModeStorageKey = currentUser
    ? `privilegedDarkMode:${currentUser.uid}`
    : null;
  const superAdminDarkModeStorageKey = currentUser
    ? `superAdminDarkMode:${currentUser.uid}`
    : null;

  useEffect(() => {
    if (!canUseDarkMode || !darkModeStorageKey) {
      setDarkMode(false);
      return;
    }

    const storedPreference = localStorage.getItem(darkModeStorageKey);
    const privilegedPreference = privilegedDarkModeStorageKey
      ? localStorage.getItem(privilegedDarkModeStorageKey)
      : null;
    const superAdminPreference = superAdminDarkModeStorageKey
      ? localStorage.getItem(superAdminDarkModeStorageKey)
      : null;
    const resolvedPreference = storedPreference ?? privilegedPreference ?? superAdminPreference;
    const darkModeEnabled = resolvedPreference === null
      ? getSystemTheme() === "dark"
      : resolvedPreference === "true";

    setDarkMode(darkModeEnabled);
    localStorage.setItem(LAST_AUTHENTICATED_THEME_KEY, darkModeEnabled ? "dark" : "light");
    if (storedPreference === null) {
      localStorage.setItem(darkModeStorageKey, String(darkModeEnabled));
    }
  }, [
    canUseDarkMode,
    darkModeStorageKey,
    privilegedDarkModeStorageKey,
    superAdminDarkModeStorageKey,
  ]);

  useEffect(() => {
    const darkModeEnabled = canUseDarkMode && darkMode;
    applyDocumentTheme(darkModeEnabled ? "dark" : "light");
  }, [canUseDarkMode, darkMode]);

  const toggleDarkMode = () => {
    if (!canUseDarkMode) return;

    setDarkMode((enabled) => {
      const nextMode = !enabled;
      if (darkModeStorageKey) {
        localStorage.setItem(darkModeStorageKey, String(nextMode));
      }
      localStorage.setItem(LAST_AUTHENTICATED_THEME_KEY, nextMode ? "dark" : "light");
      return nextMode;
    });
  };
  const now = new Date(); // Get the current date and time

  now.setHours(now.getHours() + 24);
  return (
    <div className={`flex h-screen min-h-0 w-full flex-col overflow-hidden${faqWorkspace ? ' faq-workspace' : ''}`}>
      <div className="shrink-0">
        <SandboxBanner />
      </div>
      {/* <div className="fixed bottom-5 right-5 z-50"> */}
      {/* <GeneratedSuggestionComponent /> */}
      {/* </div> */}
      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
        <Navbar
          toggleDarkMode={toggleDarkMode}
          isDarkMode={canUseDarkMode && darkMode}
          canUseDarkMode={canUseDarkMode}
        >
        <React.Suspense fallback={<BrandLoader className="min-h-[50vh] justify-center" />}>
        <Routes>
          <Route path="/faqs" element={<FaqPortal />} />
          <Route path="/faqs/article/:articleId" element={<FaqPortal />} />
          <Route path="/faqs/:slug" element={<FaqPortal />} />
          <Route path="/home" element={<Navigate to="/dashboard" replace />}></Route>
          <Route path="/dashboard" element={<OperationalDashboardRoute />}></Route>
          <Route path="/auth2/dashboard" element={<Navigate to="/dashboard" replace />}></Route>
          <Route path="/dashboard/owner/*" element={<OwnerDashboard />}></Route>
          <Route path="/owner/analytics" element={<AdvancedAnalyticsDashboard />}></Route>
          <Route path="/owner/reports" element={<AgencyReportsPage />}></Route>
          <Route path="/reports/agency" element={<AgencyReportsPage />} />
          <Route path="/settings/compliance" element={<ComplianceSettings />}></Route>
          <Route path="/settings/leave" element={<LeaveSettings />}></Route>
          <Route path="/settings/dashboard" element={<DashboardSettings />}></Route>
          <Route path="/settings/branding" element={<BrandingSettings />}></Route>
          <Route path="/settings/reports-analytics" element={<ReportsAnalyticsSettings />}></Route>
          <Route path="/settings/state-reconciliation" element={<StateReconciliationPage />}></Route>
          <Route path="/dashboard/office-manager/*" element={<OfficeManagerDashboard />}></Route>
          <Route path="/dashboard/*" element={<TeamMemberDashboard />}></Route>
          <Route
            path="/event-timer"
            element={
              <EventCountdown
                targetDate={now}
                title="Quarterly Board Meeting"
                description="Review of Q3 financial results and projection for the upcoming fiscal year."
                meetingUrl="https://zoom.us/j/123456789"
              />
            }
          ></Route>
          <Route path="unauthorized" element={<Unathorized />} />
          <Route
            path="weekly-supervision"
            element={<WeeklySupervisionList />}
          />
          <Route
            path="weekly-supervision-supervisor"
            element={<WeeklySupervisionForSupervisor />}
          />
          <Route
            path="/edit/suggestionRules/*"
            element={<SuggestionRulesComponent />}
          ></Route>
          <Route path="/drive/*" element={<DriveRoutes />} />
          <Route
            path="/participant/:id/participant-snapshot"
            element={<ParticipantSnapshotTab />}
          />
          <Route
            path="/edit/task/recurring/*"
            element={<EditRecurringTasksPage />}
          ></Route>
          <Route path="/reportsPage" element={<ReportsPage />}></Route>
          <Route path="/edit/workflows/*" element={<EditWorkflows />}></Route>
          <Route
            path="/participants"
            element={<ParticipantsPage />}
          ></Route>
          <Route
            path="/edit/participants/new"
            element={<CreateParticipantPage />}
          ></Route>
          <Route
            path="/forms/v2/"
            element={<FormAdminDashboard />}
          ></Route>
          <Route
            path="/form/v2/:formId"
            element={<ProductionFormFiller />}
          ></Route>
          <Route
            path="/edit/participants/*"
            element={<EditParticipantPage />}
          ></Route>
          <Route
            path="/edit/interims/*"
            element={<Navigate to="/automations" replace />}
          ></Route>
          0{" "}
          <Route
            path="/edit/templateFiles/*"
            element={<EditTemplateFiles />}
          ></Route>
          <Route
            path="/addtask/new/interim/template/*"
            element={<Navigate to="/automations" replace />}
          ></Route>
          <Route path="/reports/*" element={<Reports />}></Route>
          <Route path="/profile/user/:userId/*" element={<UserProfile />} />
          <Route path="/chat/:conversationId?" element={<ChatPage isSideView={false} showHeader={true} />}></Route>
          <Route path="/resources" element={<ResourceDirectory />} />
          <Route path="/resources/:resourceId" element={<ResourceDirectory />} />
          <Route path="/training" element={<TrainingPortal />} />
          <Route path="/training/manage" element={<TrainingManage />} />
          <Route path="/settings/machine-identities" element={<SuperAdminRoute />}><Route index element={<MachineIdentities />} /></Route>
          <Route path="/settings/calendar-subscription" element={<CalendarSubscriptionPage />} />
          <Route path="/training/studio" element={<TrainingAuthorRouteGuard><TrainingStudio /></TrainingAuthorRouteGuard>} />
          <Route path="/profile/*" element={<Profile />}></Route>
          <Route path="/automations" element={<AutomationPage />}></Route>
          <Route
            path="/file/new/template"
            element={<AddTemplateFile />}
          ></Route>
          <Route
            path="/addActivity/new/activity/template"
            element={<CreateNewTaskActivityType />}
          ></Route>
          <Route
            path="/task/edit/activity/template"
            element={<CreateNewTaskActivityPage />}
          ></Route>
          <Route
            path="/addActivity/new/activity/*"
            element={<CreateTaskActivityComponent />}
          ></Route>
          {/* <Route
                        path="/addtask/new/interim"
                        element={<TaskCreationPage />}
                    ></Route> */}
          <Route path="/addtask/new/*" element={<TaskCreationSelector />}>
            <Route index element={<Navigate to="interim" replace />} />
            <Route path="recurring" element={<RecurringTaskCreation />} />
            <Route path="interim" element={<TaskCreationPage />} />
          </Route>
          <Route
            path="/task/:taskId/addRequiredFile"
            element={<NewFileRequirementForm />}
          ></Route>
          <Route path="/task/:taskId" element={<TaskPage />}></Route>
          <Route path="/task/*" element={<TaskPage />}></Route>
          <Route path="/edit/file/:fileId" element={<PDFEditor />}></Route>
          <Route path="/edit/employee-file/:fileId" element={<PDFEditor />}></Route>
          <Route path="/view/file/:fileId" element={<FileViewPage />}></Route>
          <Route
            path="/view/user/file/:fileId"
            element={<UserFileViewPage />}
          ></Route>
          <Route
            path="/view/employee/file/:fileId"
            element={<EmployeeFileViewPage />}
          ></Route>
          <Route
            path="/view/file/:fileId/:fileTable"
            element={<ViewFile />}
          ></Route>
          <Route
            path="/casefile/file/:fileId"
            element={<CaseFileViewPage />}
          ></Route>
          <Route path="/form/fill" element={<FormPage />}></Route>
          <Route
            path="/form/create/:editFormId?"
            element={<FormBuilder />}
          ></Route>
          <Route
            path="/form/mapping/:editFormId?"
            element={<MappingBuilder />}
          ></Route>
          <Route path="/form/admin" element={<FormAdminPanel />}></Route>
          <Route path="/form" element={<FormPage />}></Route>
          <Route
            path="/edit/participants/bump/:participantId"
            element={<BumpParticipantForm />}
          ></Route>
          <Route path="/tags" element={<TagList />} />
          <Route
            path="/tags/new"
            element={<TagCreationForm isEdit={false} />}
          />
          <Route
            path="/tags/edit/:id"
            element={<TagCreationForm isEdit={true} />}
          />
          <Route path="/calendar" element={<MyCalendar />} />
          {/* <Route path="/tasks/*" element={<TasksPage />} /> */}
          <Route path="/tasks/*" element={<TasksPageComponent />} />
          <Route path="/employee-tasks/*" element={<EmployeeTasksPageComponent />} />
          <Route path="/issues/*" element={<PlanningRouteGuard><IssuesPageComponent /></PlanningRouteGuard>} />
          <Route path="/issue/:issueId" element={<PlanningRouteGuard><IssuePage /></PlanningRouteGuard>} />
          <Route path="/epics" element={<PlanningRouteGuard><EpicsPage /></PlanningRouteGuard>} />
          <Route path="/sprints" element={<PlanningRouteGuard><SprintsListPage /></PlanningRouteGuard>} />
          <Route path="/sprints/:sprintId" element={<PlanningRouteGuard><SprintDetailPage /></PlanningRouteGuard>} />
          <Route path="/signatures" element={<SignDocument />} />
          <Route path="/signatures/sign/:token" element={<AuthenticatedSignPage />} />
          <Route path="/notification" element={<Notificationtab />} />
          <Route path="/employeeFiles" element={<EmployeeFiles />} />
          <Route path="/forbidden" element={<Forbidden />} />
          {/* Admin FAQ Routes */}
          <Route element={<SuperAdminRoute />}>
            <Route path="/admin/faqs/analytics" element={<AdminFaqAnalytics />} />
            <Route path="/admin/faqs" element={<AdminFaqPortal />} />
            <Route path="/admin/faqs/new" element={<AdminFaqPortal />} />
            <Route path="/admin/faqs/:id/edit" element={<AdminFaqPortal />} />
          </Route>

          <Route path="/commingsoon" element={<Commingsoon />} />
          <Route path="/admin-requests" element={<AdminRequestsPanel />} />
          <Route path="/privacy-policy" element={<Privacypolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/resourcerequest" element={<Resourcerequest />} />


          <Route element={<SuperAdminRoute />}>
            {SuperAdminRoutesMap}
          </Route>
          <Route path="/dashboard/owner/automations/audit" element={<AutomationAuditDashboard />} />

          <Route
            path="/caseloadindependence"
            element={<Caseloadindependence />}
          />
          <Route
            path="/employeesurveys/:instanceId"
            element={<Employeesurveys />}
          />
          <Route
            path="/surveys/participant/:participantId?"
            element={<Surveys />}
          />
          <Route
            path="/prestormsurvey/:participantId/:instanceId"
            element={<Prestormsurvey />}
          />
          <Route
            path="/relocationsurvey/:participantId/:instanceId"
            element={<Relocationsurvey />}
          />
          <Route
            path="/poststormsurvey/:participantId/:instanceId"
            element={<Poststormsurvey />}
          />
          <Route path="/cir" element={<Cir />} />
          <Route path="/audittab" element={<Audittab />} />
          <Route
            path="/participant/:id/activity"
            element={<ParticipantActivityPage />}
          />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/channels" element={<ChannelAdminPage />} />
          <Route path="/settings/organization/security" element={<OwnerRoleEditor />} />
          <Route path="/insights" element={<Insights />} />
          <Route
            path="/external-file-request"
            element={<ExternalFileRequestHeader />}
          />
          {/* <Route path="/share-with-me-file" element={<Sharewithme/>} /> */}
          <Route path="/aging-report" element={<AgingReport />} />
          <Route path="/all-members" element={<TeamAccessGate><AllMembers /></TeamAccessGate>} />

          
          <Route path="*" element={<PageNotFound />} />
        </Routes>
        </React.Suspense>
        </Navbar>
      </div>
      {/* For Owner */}
      <Routes>
        {/* <Route path="/dashboard/owner/*" element={<OwnerDashboard />} /> */}
      </Routes>
    </div>
  );
};

export default Home;
